import { useNavigate } from 'react-router-dom'
import { DataTable, EmptyState, Skeleton, Button } from '@rb/ui'
import type { DataTableColumn } from '@rb/ui'
import { useFetch } from '../hooks/useFetch'
import styles from './AdminUsers.module.css'

interface AdminUser {
  id: string
  name: string | null
  email: string
  role: string
  created_at: string
  verified_at: string | null
}

function formatDate(s: string) {
  const d = new Date(s)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function roleLabel(role: string) {
  if (role === 'super_admin') return 'Super Admin'
  if (role === 'admin') return 'Admin'
  return 'Kunde'
}

export default function AdminUsers() {
  const navigate = useNavigate()
  const { data, loading, error, reload } = useFetch<AdminUser[]>('/admin/users')
  const users = data ?? []

  const columns: DataTableColumn<AdminUser>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => (
        <div>
          <div className={styles.userName}>{row.name || '—'}</div>
          <div className={styles.userEmail}>{row.email}</div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Rolle',
      render: (row) => <span className={styles.role}>{roleLabel(row.role)}</span>,
      width: '140px',
    },
    {
      key: 'verified_at',
      header: 'Verifiziert',
      render: (row) => (
        <span className={styles.date}>
          {row.verified_at ? formatDate(row.verified_at) : '—'}
        </span>
      ),
      width: '140px',
    },
    {
      key: 'created_at',
      header: 'Erstellt',
      render: (row) => <span className={styles.date}>{formatDate(row.created_at)}</span>,
      width: '140px',
    },
  ]

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Benutzer</h1>
      </header>

      {loading && (
        <div className={styles.skeletons}>
          <Skeleton className={styles.skeletonRow} />
          <Skeleton className={styles.skeletonRow} />
          <Skeleton className={styles.skeletonRow} />
        </div>
      )}

      {!loading && error && (
        <div className={styles.errorState}>
          <p>Fehler beim Laden</p>
          <Button variant="secondary" onClick={reload}>Erneut versuchen</Button>
        </div>
      )}

      {!loading && !error && users.length === 0 && (
        <EmptyState
          heading="Noch keine Benutzer"
          description="Registrierte Benutzer erscheinen hier."
          ctaLabel="Zu den Projekten"
          ctaTo="/admin/projects"
        />
      )}

      {!loading && !error && users.length > 0 && (
        <DataTable
          columns={columns}
          rows={users}
          getRowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/admin/users/${row.id}`)}
        />
      )}
    </div>
  )
}
